import { FormattedDate } from 'components/dates';
import { useOnClickOutside } from 'hooks';
import React, { useMemo, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled from 'styled-components';
import colors from 'theme/colors';
import Group from 'ui/group';
import Icon from 'ui/icon';
import Stack from 'ui/stack';
import Text from 'ui/text';
import './styles.css';

const Control = styled(Group)<{ disabled?: boolean }>`
  position: relative;
  background-color: ${({ theme }) => theme.colors.dark2};
  padding: 12px 16px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'arrow' : 'pointer')};
`;

const DropDown = styled.div`
  position: absolute;
  right: 0;
  bottom: 100%;
`;

interface IProps {
  label?: string;
  value?: Date;
  range?: Date[];
  selectRange?: boolean;
  onChange?: (date: Date) => void;
  onRangeChange?: (date: Date[]) => void;
  disabled?: boolean;
  cypressAttribute?: string;
}

const CalendarInput: React.FC<IProps> = ({
  value,
  range,
  onChange,
  onRangeChange,
  label,
  selectRange,
  disabled = false,
  cypressAttribute,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState(value ? new Date(value) : new Date());
  const ref = useRef(null);
  const close = () => setIsOpen(false);

  useOnClickOutside(ref, close);

  const rangeValue: Date[] | undefined = useMemo(() => {
    return range?.length ? [new Date(range[0]), new Date(range[1])] : undefined;
  }, [range]);

  const handleChange = (data: Date | Date[]) => {
    if (Array.isArray(data)) {
      onRangeChange?.(data);
      setIsOpen(false);
      return;
    }
    setDate(data);
    onChange?.(data);
    setIsOpen(false);
  };

  return (
    <Stack gap="15px" data-cy={cypressAttribute && `${cypressAttribute}-field`}>
      {label && <Text>{label}</Text>}
      {disabled ? (
        <Control disabled>
          <FormattedDate date={date} />
        </Control>
      ) : (
        <Control ref={ref} onClick={() => setIsOpen(true)} justify="space-between" align="center">
          <Text color={colors.dark4}>
            {selectRange && rangeValue ? (
              <>
                <FormattedDate date={rangeValue[0]} />
                <span> - </span>
                <FormattedDate date={rangeValue[1]} />
              </>
            ) : (
              <FormattedDate date={date} />
            )}
          </Text>

          {isOpen && (
            <DropDown data-cy={cypressAttribute}>
              <Calendar
                defaultValue={selectRange ? rangeValue : date}
                locale="en-US"
                calendarType="US"
                onChange={handleChange}
                selectRange={selectRange}
                returnValue={selectRange ? 'range' : 'start'}
              />
            </DropDown>
          )}
          <Icon icon="CalendarIcon" />
        </Control>
      )}
    </Stack>
  );
};

export default CalendarInput;
